import 'react-notion/src/styles.css'

import { Trans } from '@lingui/macro'
import { useWeb3React } from '@web3-react/core'
import { useToggleAccountDrawer } from 'components/AccountDrawer'
import { ButtonPurple } from 'components/Button'
import Checkbox from 'components/Checkbox'
import Dots from 'components/Dots'
import Modal from 'components/Modal'
import Row from 'components/Row'
import { SupportedChainId } from 'constants/chains'
import { IUserInfo } from 'graphql/competition/UserInfo'
import { useCompetitionContract } from 'hooks/useContract'
import useSelectChain from 'hooks/useSelectChain'
import React, { useEffect, useMemo, useState } from 'react'
import { NotionRenderer } from 'react-notion'
import { useIsRegistrationStarted, useReferralModal } from 'state/competition/hooks'
import { updateReferralModal, updateRegistration } from 'state/competition/reducer'
import { useAppDispatch } from 'state/hooks'
import { useTransactionAdderWithCallback } from 'state/transactions/hooks'
import { TransactionType } from 'state/transactions/types'
import styled from 'styled-components/macro'
import { CloseIcon, Divider, ThemedText } from 'theme'

import { useReferrer } from '../hooks/useReferrer'

const ReferrerBlock = styled.div`
  padding: 0.5rem;
  border-radius: 10px;
  border: 1px solid ${({ theme }) => theme.backgroundInteractive};
  background: ${({ theme }) => theme.accentFailureSoft};
`

const ReferrerDescription = styled.div``

const ReferrerAddress = styled.div`
  margin-top: 1rem;
`

const Wrapper = styled.div`
  background-color: ${({ theme }) => theme.backgroundSurface};
  border-radius: 20px;
  outline: 1px solid ${({ theme }) => theme.backgroundOutline};
  max-width: 100vw;
  padding: 16px;
  overflow-y: scroll;
  max-height: 500px;

  &::-webkit-scrollbar {
    width: 3px;
  }
  &::-webkit-scrollbar-thumb {
    width: 3px;
    background: gray;
    border-radius: 10px;
  }
  &::-webkit-scrollbar-track {
  }
`

const HiddenScroll = styled.div`
  border-radius: 20px;
  width: 100%;
  overflow-y: hidden;
  max-height: 500px;
  position: relative;
`

const CloseIconBg = styled(CloseIcon)`
  right: 10px;
  padding: 3px;
  top: 10px;
  position: absolute;
  background: ${({ theme }) => theme.backgroundOutline};
  border-radius: 50%;
`

const Important = styled(ReferrerBlock)`
  background: ${({ theme }) => theme.accentFailureSoft};
`

const ButtonWrapper = styled.div`
  display: flex;
  justify-items: center;
  justify-content: center;
  padding: 1rem;
  margin-bottom: 2rem;
`

const RegisterButtonWrapper = styled.div`
  display: flex;
  justify-items: center;
  justify-content: center;
  padding: 1rem;
`
const FlexCol = styled.div<{ width?: string; maxWidth?: string }>`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: ${({ width }) => width || ''};
  max-width: ${({ maxWidth }) => maxWidth || ''};
`

const StyledDiv = styled.div`
  max-width: 100%;

  .notion,
  .notion-text,
  .notion-page-link {
    color: ${({ theme }) => theme.textPrimary};
  }
  .notion-code {
    color: black !important;
  }
  //.notion-asset-wrapper {
  //  width: auto !important;
  //}
`

interface IModalProps {
  userInfo: IUserInfo
  rulesAccepted: { isLoading: boolean; data?: boolean; accept: () => void }
}

export const ReferralAndRules = ({ userInfo, rulesAccepted }: IModalProps) => {
  const { account, chainId } = useWeb3React()
  const selectChain = useSelectChain()
  const isRegistrationStarted = useIsRegistrationStarted(account)
  const referrer = useReferrer(userInfo, account)
  const toggleWalletDrawer = useToggleAccountDrawer()
  const competitionContract = useCompetitionContract()
  const refModal = useReferralModal()
  const isOpen = useMemo(() => {
    if (!refModal) {
      return false
    }
    return refModal.isOpen
  }, [refModal])

  const dispatch = useAppDispatch()
  const [isClosed, setIsClosed] = useState(true)
  const addTransactionWithCb = useTransactionAdderWithCallback()
  const [accept, setAccept] = useState({
    read: false,
    accept: false,
  })

  const [notion, setNotion] = useState()

  const registerBtn = useMemo(() => {
    if (!account || userInfo.isLoading || userInfo.data?.user) return null
    if (isRegistrationStarted) {
      return (
        <ButtonWrapper>
          <ButtonPurple style={{ marginTop: '1.25rem', padding: '9px 20px', width: 'auto' }} disabled={true}>
            <Trans>
              Registration in progress
              <Dots />
            </Trans>
          </ButtonPurple>
        </ButtonWrapper>
      )
    }

    return (
      <ButtonWrapper>
        <ButtonPurple
          style={{ marginTop: '1.25rem', padding: '9px 20px', width: 'auto' }}
          onClick={() => setIsClosed(false)}
        >
          <Trans>Register in competition</Trans>
        </ButtonPurple>
      </ButtonWrapper>
    )
  }, [account, userInfo, isRegistrationStarted])

  useEffect(() => {
    if (rulesAccepted.data && !isClosed) {
      dispatch(updateReferralModal({ isOpen: true, data: { isRules: false } }))
      return
    }
    if (!rulesAccepted.isLoading && !rulesAccepted.data && !isClosed) {
      dispatch(updateReferralModal({ isOpen: true, data: { isRules: false } }))
      return
    }
    if (rulesAccepted.data) {
      dispatch(updateReferralModal({ isOpen: false, data: { isRules: false } }))
      return
    }
    dispatch(updateReferralModal({ isOpen: !isClosed, data: { isRules: false } }))
  }, [isClosed, rulesAccepted])

  async function fetchNotion() {
    const data = await fetch('https://notion-api.splitbee.io/v1/page/22651e596c198067a24fec1e45a42ee2').then((res) =>
      res.json()
    )
    setNotion(data)
  }

  useEffect(() => {
    fetchNotion()
  }, [])

  const onDismiss = () => {
    setIsClosed(true)
    dispatch(updateReferralModal({ isOpen: false, data: { isRules: false } }))
  }

  const handleSwitchNetwork = async () => {
    if (chainId && chainId !== SupportedChainId.XDC) {
      const result = await selectChain(SupportedChainId.XDC)
    }
  }

  const handleRegister = async () => {
    if (!account || !competitionContract) {
      return
    }
    try {
      const tx = await competitionContract.register(referrer.data.front || 0)
      dispatch(updateRegistration({ account }))
      setIsClosed(true)

      addTransactionWithCb(tx, {
        type: TransactionType.REGISTER_COMPETITION,
      })

      if (!tx) {
        return
      }
    } catch (e) {
      console.error(e)
    }

    // поставить на ожидание транзу
  }

  const referrerBlock = useMemo(() => {
    if (referrer.isLoading) {
      return null
    }

    if (referrer.data.query) {
      if (referrer.data.contract && referrer.data.contract !== referrer.data.query) {
        return (
          <ReferrerBlock>
            <ReferrerDescription>Referral code already has been applied on contract. </ReferrerDescription>
            <ReferrerAddress>Ref code: {referrer.data.contract}</ReferrerAddress>
            {/*<ReferrerAddress*/}
            {/*  target="_blank"*/}
            {/*  href={getExplorerLink(ChainId.XDC, referrer.data.contract, ExplorerDataType.ADDRESS)}*/}
            {/*>*/}
            {/*  {referrer.data.contract}*/}
            {/*</ReferrerAddress>*/}
          </ReferrerBlock>
        )
      }
      if (referrer.data.front && referrer.data.front !== referrer.data.query) {
        return (
          <ReferrerBlock>
            <ReferrerDescription>
              Referral code has already been saved on your browser. You will get extra 15% from trading volume to your
              score.
            </ReferrerDescription>

            <ReferrerAddress>Referral code: {referrer.data.front}</ReferrerAddress>
          </ReferrerBlock>
        )
      }
      if (userInfo.data?.user?.refCode === referrer.data.query) {
        return <ReferrerBlock>You can not use your own referral code!</ReferrerBlock>
      }

      return (
        <ReferrerBlock>
          <ReferrerDescription>
            Congratulations! You have came here by referral link. You will get extra 15% from trading volume to your
            score.
          </ReferrerDescription>
          <ReferrerAddress>Referral code: {referrer.data.query}</ReferrerAddress>
        </ReferrerBlock>
      )
    }
    if (referrer.data.front) {
      return (
        <ReferrerBlock>
          <ReferrerDescription>
            Referrer address already has been saved on your browser. You will get extra 15% from trading volume to your
            score.
          </ReferrerDescription>
          Ref code:
          <ReferrerAddress>{referrer.data.front}</ReferrerAddress>
        </ReferrerBlock>
      )
    }

    return null
  }, [referrer])

  if (!account) {
    return (
      <ButtonWrapper>
        <ButtonPurple style={{ marginTop: '1.25rem', padding: '9px 20px', width: 'auto' }} onClick={toggleWalletDrawer}>
          <Trans>Connect a wallet to start competition</Trans>
        </ButtonPurple>
      </ButtonWrapper>
    )
  }

  if (!rulesAccepted && !account) {
    return (
      <ButtonWrapper>
        <ButtonPurple style={{ marginTop: '1.25rem', padding: '9px 20px', width: 'auto' }} onClick={toggleWalletDrawer}>
          <Trans>Connect a wallet to start competition</Trans>
        </ButtonPurple>
      </ButtonWrapper>
    )
  }

  if (!rulesAccepted && chainId !== SupportedChainId.XDC) {
    return (
      <ButtonWrapper>
        <ButtonPurple
          style={{ marginTop: '1.25rem', padding: '9px 20px', width: 'auto' }}
          onClick={handleSwitchNetwork}
        >
          <Trans>Switch to the mainnet</Trans>
        </ButtonPurple>
      </ButtonWrapper>
    )
  }

  return (
    <>
      {registerBtn}

      <Modal isOpen={isOpen} $scrollOverlay={true} onDismiss={onDismiss} maxHeight={50} maxWidth={800}>
        <HiddenScroll>
          <CloseIconBg onClick={onDismiss} data-testid="confirmation-close-icon" />
          <Wrapper>
            <FlexCol>
              <Row>
                <Row justify="center" marginLeft="24px">
                  <div tabIndex={1}></div>
                  <ThemedText.SubHeader>Terms and Conditions</ThemedText.SubHeader>
                </Row>
              </Row>
              <Important>
                Important! Your trading volume will only be counted after accepting and signing the terms and
                conditions.
              </Important>
              {notion && (
                <StyledDiv>
                  <NotionRenderer blockMap={notion} />
                </StyledDiv>
              )}
              {referrerBlock}

              {!rulesAccepted.data && (
                <>
                  <Divider />
                  <Checkbox
                    id="checkit"
                    label="I've read all terms and conditions."
                    value=""
                    checked={accept.read}
                    onChange={() => {
                      setAccept((v) => ({ ...v, read: !accept.read }))
                    }}
                  />
                  <Checkbox
                    id="infos"
                    label="By participating in the competition and invoking/calling the trading competition contract, I acknowledge and consent to abide by the terms and conditions established by XSwap."
                    value=""
                    checked={accept.accept}
                    onChange={() => {
                      setAccept((v) => ({ ...v, accept: !accept.accept }))
                    }}
                  />
                </>
              )}
            </FlexCol>
          </Wrapper>
        </HiddenScroll>
        {!rulesAccepted.data && (
          <RegisterButtonWrapper>
            <ButtonPurple
              disabled={!accept.accept || !accept.read}
              style={{ padding: '9px 20px', width: 'auto' }}
              onClick={handleRegister}
            >
              <Trans>Accept & Register</Trans>
            </ButtonPurple>
          </RegisterButtonWrapper>
        )}
      </Modal>
    </>
  )
}
